import {
  AlertTriangle,
  ArrowLeft,
  Bell,
  Briefcase,
  Building2, Button, Calendar,
  Clock,
  Edit,
  FileText,
  GraduationCap,
  Languages,
  Mail,
  MapPin,
  Phone,
  RefreshCw,
  Stethoscope,
  User,
  UserMinus,
  formatPhoneNumberForDisplay
} from "@ews-admin/global-design-system";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { InlineLoader } from "../../components/atoms/InlineLoader";
import { ConfirmationModal } from "../../components/molecules/ConfirmationModal/ConfirmationModal";
import { PageHeader } from "../../components/molecules/PageHeader";
import { StaffForm } from "../../components/molecules/StaffForm";
import useManager from "../../hooks/useManager";
import { doctorApi } from "../../services/doctor.api";
import secretaryApi from "../../services/secretary.api";
import { fetchStatsRequest } from "../../store/reducers/dashboardSlice";
import { CreateDoctorPayload, ProvidedService } from "../../types/doctor.definition";
import { Specialty } from "../../types/specialty.definition";
import { Staff } from "../../types/staff.definition";
import { convertToDateISOString } from "../../utils/date.utils";
import { formatMoney, toIntlLocale } from "../../utils/format.utils";

export const StaffDetails: React.FC = () => {
  const { role, staffId } = useParams<{ role: string; staffId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const manager = useManager();
  const { t, i18n } = useTranslation();
  const facilityId = Number(manager.healthcareFacility?.id);

  // Seed with data from the list navigation state so flags like
  // requiresPasswordChange are visible immediately (before the detail fetch).
  const seedStaff = (location.state as { staff?: Staff } | null)?.staff ?? null;
  const [staff, setStaff] = useState<Staff | null>(seedStaff);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Edit
  const [showEditModal, setShowEditModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);

  // Password resend
  const [isResendingPassword, setIsResendingPassword] = useState(false);
  const [resendPasswordSuccess, setResendPasswordSuccess] = useState(false);
  const [resendPasswordError, setResendPasswordError] = useState<string | null>(null);

  // Danger zone confirmations
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showUnassignConfirmation, setShowUnassignConfirmation] = useState(false);

  const isDoctor = role === "doctor";
  const isSecretary = role === "secretary";

  useEffect(() => {
    const fetchStaff = async () => {
      if (!staffId) return;
      setIsLoading(true);
      setError(null);
      try {
        let fetched: Staff;
        if (isDoctor) {
          fetched = (await doctorApi.getDoctorById(staffId)) as unknown as Staff;
        } else {
          fetched = (await secretaryApi.getSecretaryById(staffId)) as unknown as Staff;
        }
        // Merge seed flags (e.g. requiresPasswordChange) that the detail
        // endpoint may not return, so the yellow section is never lost.
        setStaff((prev) => ({ ...prev, ...fetched }));
      } catch {
        setError(t("staff.manage.details.fetchError", "Failed to load staff details."));
      } finally {
        setIsLoading(false);
      }
    };
    fetchStaff();
  }, [staffId, isDoctor, t]);

  const handleResendTemporaryPassword = async () => {
    if (!staff?.userId) return;
    setIsResendingPassword(true);
    setResendPasswordError(null);
    setResendPasswordSuccess(false);
    try {
      if (isDoctor) {
        await doctorApi.resendTemporaryPassword(staff.userId);
      } else {
        await secretaryApi.resendTemporaryPassword(staff.userId);
      }
      setResendPasswordSuccess(true);
      setTimeout(() => setResendPasswordSuccess(false), 3000);
    } catch (err: unknown) {
      const msg =
        err && typeof err === "object" && "message" in err
          ? (err.message as string)
          : t("staff.manage.details.resendPasswordError", "Failed to resend temporary password.");
      setResendPasswordError(msg);
    } finally {
      setIsResendingPassword(false);
    }
  };

  const handleStaffSubmit = async (formData: Partial<CreateDoctorPayload>) => {
    if (!staff) return;
    setIsSubmitting(true);
    setSubmitSuccess(null);
    try {
      if (isDoctor) {
        const updatePayload: Record<string, unknown> = {};
        if (formData.firstName !== staff.firstName) updatePayload.firstName = formData.firstName;
        if (formData.lastName !== staff.lastName) updatePayload.lastName = formData.lastName;
        if (formData.civility !== staff.civility) updatePayload.civility = formData.civility;
        if (formData.phoneNumber !== staff.phoneNumber) updatePayload.phoneNumber = formData.phoneNumber;
        if (formData.photoUrl !== staff.photoUrl) updatePayload.photoUrl = formData.photoUrl;
        if (formData.hospitalName !== staff.hospitalName) updatePayload.hospitalName = formData.hospitalName;
        if (formData.diplomas !== staff.diplomas) updatePayload.diplomas = formData.diplomas;
        if (formData.refNumber !== staff.refNumber) updatePayload.refNumber = formData.refNumber;
        if (formData.paymentPhoneNumber !== staff.paymentPhoneNumber)
          updatePayload.paymentPhoneNumber = formData.paymentPhoneNumber;
        if (formData.dateOfBirth) {
          const formatted = convertToDateISOString(formData.dateOfBirth as string);
          if (formatted !== staff.dateOfBirth) updatePayload.dateOfBirth = formatted;
        }
        if (formData.careerStartYear) {
          const formatted = convertToDateISOString(formData.careerStartYear as string);
          if (formatted !== staff.careerStartYear) updatePayload.careerStartYear = formatted;
        }
        if (
          JSON.stringify(formData.preferredNotificationMethods) !==
          JSON.stringify(staff.preferredNotificationMethods)
        ) {
          updatePayload.preferredNotificationMethods = formData.preferredNotificationMethods;
        }
        if (JSON.stringify(formData.providedServices) !== JSON.stringify(staff.providedServices)) {
          updatePayload.providedServices = formData.providedServices as ProvidedService[];
        }
        const specialtyIds = ((formData.specialties || []) as Specialty[])
          .map((s) => (s.id ? Number(s.id) : null))
          .filter((id): id is number => id !== null);
        const existingSpecialtyIds = ((staff.specialties || []) as Specialty[])
          .map((s) => (s.id ? Number(s.id) : null))
          .filter((id): id is number => id !== null);
        if (JSON.stringify(specialtyIds.sort()) !== JSON.stringify(existingSpecialtyIds.sort())) {
          updatePayload.specialtyIds = specialtyIds;
        }
        await doctorApi.updateDoctor(staff.id, updatePayload);
        setSubmitSuccess(
          t("staff.manage.form.success.doctorUpdated", { name: `${formData.firstName} ${formData.lastName}` }),
        );
      } else {
        const updateData: Record<string, unknown> = {};
        if (formData.firstName !== staff.firstName) updateData.firstName = formData.firstName;
        if (formData.lastName !== staff.lastName) updateData.lastName = formData.lastName;
        if (formData.civility !== staff.civility) updateData.civility = formData.civility;
        if (formData.phoneNumber !== staff.phoneNumber) updateData.phoneNumber = formData.phoneNumber;
        if (formData.photoUrl !== staff.photoUrl) updateData.photoUrl = formData.photoUrl;
        if (formData.dateOfBirth) {
          const formatted = convertToDateISOString(formData.dateOfBirth as string);
          if (formatted !== staff.dateOfBirth) updateData.dateOfBirth = formatted;
        }
        if (
          JSON.stringify(formData.preferredNotificationMethods) !==
          JSON.stringify(staff.preferredNotificationMethods)
        ) {
          updateData.preferredNotificationMethods = formData.preferredNotificationMethods;
        }
        updateData.healthcareFacilityId = facilityId;
        await secretaryApi.updateSecretary(staff.id, updateData);
        setSubmitSuccess(
          t("staff.manage.form.success.secretaryUpdated", {
            name: `${formData.firstName} ${formData.lastName}`,
          }),
        );
      }

      // Refresh staff data after update; preserve flags the detail API may omit
      const updated = isDoctor
        ? ((await doctorApi.getDoctorById(staff.id)) as unknown as Staff)
        : ((await secretaryApi.getSecretaryById(staff.id)) as unknown as Staff);
      setStaff((prev) => ({ ...prev, ...updated }));

      if (manager.healthcareFacility?.id) {
        dispatch(fetchStatsRequest({ facilityId: manager.healthcareFacility.id }));
      }

      setShowEditModal(false);
    } catch (err) {
      console.error("Error updating staff:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const translateServiceCode = (code: string): string => {
    const map: Record<string, string> = {
      ONLINE: t("staff.manage.details.serviceTypes.online"),
      CLINIC: t("staff.manage.details.serviceTypes.clinic"),
      HOME: t("staff.manage.details.serviceTypes.home"),
    };
    return map[code] || code;
  };

  const translateLanguageCode = (code: string): string => {
    const map: Record<string, string> = {
      WO: t("staff.manage.details.languages.wo"),
      EN: t("staff.manage.details.languages.en"),
      AR: t("staff.manage.details.languages.ar"),
      FR: t("staff.manage.details.languages.fr"),
    };
    return map[code] || code;
  };

  const translateNotificationMethod = (method: string): string => {
    const map: Record<string, string> = {
      EMAIL: t("staff.manage.form.notificationEmail"),
      SMS: t("staff.manage.form.notificationSMS"),
    };
    return map[method] || method;
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <InlineLoader />
      </div>
    );
  }

  if (error || !staff) {
    return (
      <div className="p-6 min-h-screen bg-neutral-50">
        <Button variant="ghost" onClick={() => navigate("/manager/staff")} className="mb-4">
          <ArrowLeft className="mr-2 w-4 h-4" />
          {t("staff.manage.details.backToStaff")}
        </Button>
        <div className="p-4 text-sm text-red-700 bg-red-50 rounded-lg border border-red-200">
          {error || t("staff.manage.details.notFound", "Staff member not found.")}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4 min-h-screen bg-neutral-50 sm:p-6 sm:space-y-6">
      {/* Page Header */}
      <PageHeader
        title={`${isDoctor ? "Dr. " : ""}${staff.firstName} ${staff.lastName}`}
        description={
          isSecretary
            ? t("staff.manage.list.secretary")
            : staff.specialties && staff.specialties.length > 0
              ? staff.specialties.map((s) => s.label || s.code).join(" • ")
              : t("staff.manage.details.medicalProfessional")
        }
        icon={User}
        actions={
          <div className="flex items-center gap-2 sm:gap-3">
            <Button variant="ghost" onClick={() => navigate("/manager/staff")}>
              <ArrowLeft className="mr-2 w-4 h-4" />
              {t("staff.manage.details.backToStaff")}
            </Button>
            <Button onClick={() => setShowEditModal(true)}>
              <Edit className="mr-2 w-4 h-4" />
              {t("staff.manage.details.editStaff")}
            </Button>
          </div>
        }
      />

      <div className="mx-auto max-w-4xl space-y-4 sm:space-y-6">
        {/* Profile Card */}
        <div
          className={`p-4 rounded-xl sm:p-6 ${isSecretary
              ? "bg-ews-secondary"
              : "bg-ews-primary"
            }`}
        >
          <div className="flex flex-col items-center text-center sm:flex-row sm:text-left sm:items-start">
            <div className="flex-shrink-0 mb-4 sm:mb-0 sm:mr-6">
              {staff.photoUrl ? (
                <img
                  src={staff.photoUrl}
                  alt={`${staff.firstName} ${staff.lastName}`}
                  className="object-cover w-20 h-20 rounded-2xl border-4 shadow-xl border-white/20"
                />
              ) : (
                <div className="flex justify-center items-center w-20 h-20 rounded-2xl border-4 shadow-xl backdrop-blur-sm bg-white/20 border-white/20">
                  <User className="w-10 h-10 text-white" />
                </div>
              )}
            </div>
            <div className="flex-1 text-white">
              <h2 className="mb-1 text-xl font-bold sm:text-2xl md:text-3xl">
                {isDoctor && "Dr. "}
                {staff.firstName} {staff.lastName}
              </h2>
              <p className="mb-3 text-sm text-white/90">
                {isSecretary
                  ? t("staff.manage.list.secretary")
                  : staff.specialties && staff.specialties.length > 0
                    ? staff.specialties.map((s) => s.label || s.code).join(" • ")
                    : t("staff.manage.details.medicalProfessional")}
              </p>
              <div className="flex flex-col flex-wrap gap-2 text-sm sm:flex-row sm:gap-4">
                {(staff.contactInfo?.email || staff.email) && (
                  <div className="flex items-center gap-2">
                    <Mail className="flex-shrink-0 w-4 h-4" />
                    <span className="truncate text-white/90">
                      {staff.contactInfo?.email || staff.email}
                    </span>
                  </div>
                )}
                {(staff.contactInfo?.phoneNumber || staff.phoneNumber) && (
                  <div className="flex items-center gap-2">
                    <Phone className="flex-shrink-0 w-4 h-4" />
                    <span className="text-white/90">
                      {formatPhoneNumberForDisplay(
                        staff.contactInfo?.phoneNumber || staff.phoneNumber
                      )}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Personal Information */}
        <div className="p-4 bg-white rounded-xl border shadow-sm sm:p-6 border-neutral-200">
          <div className="flex gap-3 items-center mb-4 sm:mb-6">
            <div
              className={`flex justify-center items-center w-9 h-9 rounded-lg ${isSecretary ? "bg-secondary-50" : "bg-ews-primary-light"
                }`}
            >
              <User
                className={`w-5 h-5 ${isSecretary ? "text-ews-secondary" : "text-ews-primary"}`}
              />
            </div>
            <h3 className="text-lg font-semibold text-neutral-900">
              {t("staff.manage.details.personalInfo")}
            </h3>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <InfoItem
              icon={User}
              label={t("staff.manage.details.fullName")}
              value={`${staff.firstName} ${staff.lastName}`}
            />
            <InfoItem
              icon={Calendar}
              label={t("staff.manage.details.dateOfBirth")}
              value={
                staff.dateOfBirth
                  ? new Date(staff.dateOfBirth).toLocaleDateString(toIntlLocale(i18n.language), {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  })
                  : t("staff.manage.details.notSpecified")
              }
            />
            <InfoItem
              icon={FileText}
              label={t("staff.manage.details.referenceNumber")}
              value={staff.refNumber || t("staff.manage.details.notSpecified")}
            />
            {isDoctor && (
              <InfoItem
                icon={Briefcase}
                label={t("staff.manage.details.careerStartYear")}
                value={
                  staff.careerStartYear
                    ? new Date(staff.careerStartYear).getFullYear().toString()
                    : t("staff.manage.details.notSpecified")
                }
              />
            )}
          </div>
        </div>

        {/* Professional Information — doctors only */}
        {isDoctor && (
          <div className="p-4 bg-white rounded-xl border shadow-sm sm:p-6 border-neutral-200">
            <div className="flex gap-3 items-center mb-4 sm:mb-6">
              <div className="flex justify-center items-center w-9 h-9 rounded-lg bg-secondary-100">
                <Stethoscope className="w-5 h-5 text-secondary-600" />
              </div>
              <h3 className="text-lg font-semibold text-neutral-900">
                {t("staff.manage.details.professionalInfo")}
              </h3>
            </div>
            <div className="space-y-4">
              <InfoItem
                icon={Building2}
                label={t("staff.manage.details.primaryFacility")}
                value={
                  staff.healthcareFacilities?.[0]?.name ||
                  staff.hospitalName ||
                  t("staff.manage.details.notSpecified")
                }
              />
              {staff.specialties && staff.specialties.length > 0 && (
                <TagListItem
                  icon={GraduationCap}
                  label={t("staff.manage.details.specialties")}
                  tags={staff.specialties.map((s) => s.label || s.code || "Unknown")}
                  variant="primary"
                />
              )}
              {staff.providedServices && staff.providedServices.length > 0 && (
                <TagListItem
                  icon={Briefcase}
                  label={t("staff.manage.details.services")}
                  tags={staff.providedServices.map(
                    (s) => `${translateServiceCode(s.code)} - ${formatMoney(s.price, i18n.language)}`,
                  )}
                  variant="secondary"
                />
              )}
              {staff.nextAvailability && (
                <InfoItem
                  icon={Clock}
                  label={t("staff.manage.details.nextAvailability")}
                  value={new Date(staff.nextAvailability).toLocaleString(toIntlLocale(i18n.language), {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                    hour: "numeric",
                    minute: "2-digit",
                  })}
                />
              )}
              {staff.doctorSpokenLanguages && staff.doctorSpokenLanguages.length > 0 && (
                <TagListItem
                  icon={Languages}
                  label={t("staff.manage.details.spokenLanguages")}
                  tags={staff.doctorSpokenLanguages.map(translateLanguageCode)}
                  variant="neutral"
                />
              )}
              {staff.preferredNotificationMethods &&
                staff.preferredNotificationMethods.length > 0 && (
                  <TagListItem
                    icon={Bell}
                    label={t("staff.manage.details.notificationPreferences")}
                    tags={staff.preferredNotificationMethods.map(translateNotificationMethod)}
                    variant="neutral"
                  />
                )}
            </div>
          </div>
        )}

        {/* Location */}
        {(staff.contactInfo?.location || staff.location) && (
          <LocationCard location={staff.contactInfo?.location || staff.location} t={t} />
        )}

        {/* Biography */}
        {staff.biography && (
          <div className="p-4 bg-white rounded-xl border shadow-sm sm:p-6 border-neutral-200">
            <div className="flex gap-3 items-center mb-4">
              <div className="flex justify-center items-center w-9 h-9 rounded-lg bg-neutral-100">
                <FileText className="w-5 h-5 text-neutral-600" />
              </div>
              <h3 className="text-lg font-semibold text-neutral-900">
                {t("staff.manage.details.biography")}
              </h3>
            </div>
            <div className="p-4 text-sm leading-relaxed rounded-lg border text-neutral-700 bg-neutral-50 border-neutral-100">
              {staff.biography}
            </div>
          </div>
        )}

        {/* Password Change Required */}
        {staff.requiresPasswordChange && (
          <div className="p-4 bg-yellow-50 rounded-xl border border-yellow-200 sm:p-6">
            <div className="flex gap-3 items-start">
              <div className="flex-shrink-0">
                <div className="flex justify-center items-center w-9 h-9 bg-yellow-100 rounded-full">
                  <RefreshCw className="w-5 h-5 text-yellow-600" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="mb-1 text-sm font-semibold text-yellow-900">
                  {t("staff.manage.details.passwordChangeRequired", "Password Change Required")}
                </h4>
                <p className="mb-4 text-xs text-yellow-700">
                  {t(
                    "staff.manage.details.passwordChangeRequiredDescription",
                    "This staff member needs to set their password. A temporary password has been sent to their email.",
                  )}
                </p>
                {resendPasswordSuccess && (
                  <div className="p-2 mb-3 text-xs text-green-700 bg-green-100 rounded border border-green-300">
                    {t("staff.manage.details.resendPasswordSuccess", "Temporary password sent successfully!")}
                  </div>
                )}
                {resendPasswordError && (
                  <div className="p-2 mb-3 text-xs text-red-700 bg-red-100 rounded border border-red-300">
                    {resendPasswordError}
                  </div>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleResendTemporaryPassword}
                  loading={isResendingPassword}
                  disabled={isResendingPassword}
                >
                  <RefreshCw className="mr-2 w-4 h-4" />
                  {t("staff.manage.details.resendTemporaryPassword", "Resend Temporary Password")}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Danger Zone — secretaries: unassign */}
        {isSecretary && (
          <div className="p-4 rounded-xl border border-orange-200 bg-orange-50 sm:p-6">
            <div className="flex gap-2 items-center mb-2">
              <AlertTriangle className="w-5 h-5 text-orange-600 flex-shrink-0" />
              <h3 className="text-sm font-semibold text-orange-700 uppercase tracking-wide">
                {t("staff.manage.details.dangerZone", "Danger Zone")}
              </h3>
            </div>
            <p className="mb-4 text-xs text-orange-600 leading-relaxed">
              {t(
                "staff.manage.details.unassignSecretaryWarning",
                "Removes this secretary from your facility. The account is preserved and can be reassigned to another facility.",
              )}
            </p>
            <Button
              variant="ghost"
              size="sm"
              className="border border-orange-300 text-orange-600 hover:bg-orange-100 hover:border-orange-400"
              onClick={() => setShowUnassignConfirmation(true)}
            >
              <UserMinus className="mr-2 w-4 h-4" />
              {t("staff.manage.details.unassignSecretary", "Remove from facility")}
            </Button>
          </div>
        )}

        {/* Danger Zone — doctors: unassign */}
        {isDoctor && (
          <div className="p-4 rounded-xl border border-orange-200 bg-orange-50 sm:p-6">
            <div className="flex gap-2 items-center mb-2">
              <AlertTriangle className="w-5 h-5 text-orange-600 flex-shrink-0" />
              <h3 className="text-sm font-semibold text-orange-700 uppercase tracking-wide">
                {t("staff.manage.details.dangerZone", "Danger Zone")}
              </h3>
            </div>
            <p className="mb-4 text-xs text-orange-600 leading-relaxed">
              {t(
                "staff.manage.details.unassignWarning",
                "Removes this doctor from your facility. The doctor account is preserved and can be reassigned to another facility.",
              )}
            </p>
            <Button
              variant="ghost"
              size="sm"
              className="border border-orange-300 text-orange-600 hover:bg-orange-100 hover:border-orange-400"
              onClick={() => setShowUnassignConfirmation(true)}
            >
              <UserMinus className="mr-2 w-4 h-4" />
              {t("staff.manage.details.unassignDoctor", "Remove from facility")}
            </Button>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      <StaffForm
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSubmitSuccess(null);
        }}
        onSubmit={handleStaffSubmit}
        editingStaff={staff}
        isSubmitting={isSubmitting}
        successMessage={submitSuccess}
      />

      {/* Delete Confirmation */}
      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        onCancel={() => setShowDeleteConfirmation(false)}
        onConfirm={async () => {
          try {
            await secretaryApi.deleteSecretary(staff.id);
            navigate("/manager/staff");
          } catch (err) {
            console.error("Error deleting staff:", err);
          } finally {
            setShowDeleteConfirmation(false);
          }
        }}
        title={t("staff.manage.delete.title")}
        message={t("staff.manage.delete.message", {
          firstName: staff.firstName,
          lastName: staff.lastName,
        })}
        variant="danger"
      />

      {/* Unassign Confirmation */}
      <ConfirmationModal
        isOpen={showUnassignConfirmation}
        onCancel={() => setShowUnassignConfirmation(false)}
        onConfirm={async () => {
          try {
            if (isDoctor) {
              await doctorApi.unassignFromFacility(facilityId, staff.id);
            } else {
              await secretaryApi.unassignFromFacility(facilityId, staff.id);
            }
            navigate("/manager/staff");
          } catch (err) {
            console.error("Error unassigning staff:", err);
          } finally {
            setShowUnassignConfirmation(false);
          }
        }}
        title={t(
          isDoctor ? "staff.manage.unassign.title" : "staff.manage.unassignSecretary.title",
          isDoctor ? "Remove doctor from facility" : "Remove secretary from facility",
        )}
        message={t(
          isDoctor ? "staff.manage.unassign.message" : "staff.manage.unassignSecretary.message",
          {
            firstName: staff.firstName,
            lastName: staff.lastName,
            defaultValue: isDoctor
              ? "Remove Dr. {{firstName}} {{lastName}} from your facility? Their account will not be deleted."
              : "Remove {{firstName}} {{lastName}} from your facility? Their account will not be deleted.",
          },
        )}
        confirmLabel={t("staff.manage.unassign.confirm", "Remove")}
        variant="warning"
      />
    </div>
  );
};

// ─── Helper components ────────────────────────────────────────────────────────

interface InfoItemProps {
  icon: React.FC<{ className?: string }>;
  label: string;
  value: string;
}

const InfoItem: React.FC<InfoItemProps> = ({ icon: Icon, label, value }) => (
  <div className="flex gap-3 items-start">
    <div className="flex-shrink-0 mt-0.5">
      <Icon className="w-4 h-4 text-neutral-400" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-xs font-medium tracking-wide uppercase text-neutral-500">{label}</p>
      <p className="mt-1 text-sm font-medium text-neutral-900 break-words">{value}</p>
    </div>
  </div>
);

interface TagListItemProps {
  icon: React.FC<{ className?: string }>;
  label: string;
  tags: string[];
  variant: "primary" | "secondary" | "neutral";
}

const TagListItem: React.FC<TagListItemProps> = ({ icon: Icon, label, tags, variant }) => {
  const variantClasses = {
    primary: "bg-primary-100 text-primary-700",
    secondary: "bg-secondary-100 text-secondary-700",
    neutral: "bg-neutral-100 text-neutral-700",
  };
  return (
    <div className="flex gap-3 items-start">
      <div className="flex-shrink-0 mt-0.5">
        <Icon className="w-4 h-4 text-neutral-400" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="mb-2 text-xs font-medium tracking-wide uppercase text-neutral-500">{label}</p>
        <div className="flex flex-wrap gap-2">
          {tags.map((tag, idx) => (
            <span
              key={idx}
              className={`px-2.5 py-1 text-xs font-medium rounded-full break-words ${variantClasses[variant]}`}
            >
              {tag}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

interface LocationCardProps {
  location:
  | {
    address?: string;
    city?: string;
    state?: string;
    country?: string;
    zip?: string;
    coordinates?: { lat: number; lng: number } | null;
  }
  | undefined;
  t: (key: string) => string;
}

const LocationCard: React.FC<LocationCardProps> = ({ location, t }) => {
  if (!location) return null;
  return (
    <div className="p-4 bg-white rounded-xl border shadow-sm sm:p-6 border-neutral-200">
      <div className="flex gap-3 items-center mb-4 sm:mb-6">
        <div className="flex justify-center items-center w-9 h-9 rounded-lg bg-ews-primary-light">
          <MapPin className="w-5 h-5 text-ews-primary" />
        </div>
        <h3 className="text-lg font-semibold text-neutral-900">
          {t("staff.manage.details.contactLocation")}
        </h3>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {location.address && (
          <InfoItem icon={MapPin} label={t("staff.manage.details.address")} value={location.address} />
        )}
        {location.city && (
          <InfoItem icon={MapPin} label={t("staff.manage.details.city")} value={location.city} />
        )}
        {location.state && (
          <InfoItem icon={MapPin} label={t("staff.manage.details.state")} value={location.state} />
        )}
        {location.country && (
          <InfoItem icon={MapPin} label={t("staff.manage.details.country")} value={location.country} />
        )}
        {location.zip && (
          <InfoItem icon={MapPin} label={t("staff.manage.details.zipCode")} value={location.zip} />
        )}
        {location.coordinates && (
          <div className="flex gap-3 items-start sm:col-span-2">
            <div className="flex-shrink-0 mt-0.5">
              <MapPin className="w-4 h-4 text-neutral-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium tracking-wide uppercase text-neutral-500">
                {t("staff.manage.details.coordinates")}
              </p>
              <p className="mt-1 text-sm font-medium text-neutral-900 font-mono break-all">
                {location.coordinates.lat.toFixed(4)}, {location.coordinates.lng.toFixed(4)}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StaffDetails;
